import React, { useMemo, useCallback } from "react";
import { Table } from "antd";
import { useTender } from "../../../contexts/TenderContext";
import {
  geoToberWallSpecifications,
  geoToberWallFieldToSpecId,
  BuildUpItem,
  GEO_WALL_BUILD_UP_ITEM_CODE_MAP,
  TOBERMORE_BUILD_UP_ITEM_CODE_MAP,
} from "../../../mock/geo-tober-wall-build-up";
import { useItemLibrary } from "../../../hooks/useItemLibrary";

// Inverted map: specId -> field name in geoToberWallTable
const specIdToField: Record<string, string> = {};
Object.entries(geoToberWallFieldToSpecId).forEach(([field, specId]) => {
  specIdToField[specId] = field;
});

const GeoToberWallQuants: React.FC = () => {
  const { state: tender } = useTender();

  const { data: itemLibraryData } = useItemLibrary(tender.region || "");

  const getRateFromLibrary = useCallback(
    (evaluateCode: string): number => {
      const items = itemLibraryData as unknown as any[];
      if (!Array.isArray(items) || items.length === 0) return 0;
      const match = items.find(
        (lib) =>
          lib.resourceCode?.trim().toUpperCase() ===
          evaluateCode.trim().toUpperCase(),
      );
      return match?.nettRate ?? 0;
    },
    [itemLibraryData],
  );

  // Get all unique build-up items from persisted API dimension data (real imported items)
  const allBuildUpItems = useMemo(() => {
    const geoToberWallBuildUpDimensions = ((tender?.groundworks as any)?.geoToberWallBuildUpDimensions || {}) as Record<string, Array<{ id?: string; description: string; net: number; unit: string }>>;
    const itemsMap = new Map<string, BuildUpItem>();
    Object.values(geoToberWallBuildUpDimensions).forEach((items) => {
      items.forEach((item) => {
        const itemId = item.id || '';
        if (itemId && !itemsMap.has(itemId)) {
          itemsMap.set(itemId, { id: itemId, description: item.description, net: item.net, unit: item.unit, rate: 0, total: 0 } as BuildUpItem);
        }
      });
    });
    return Array.from(itemsMap.values());
  }, [(tender?.groundworks as any)?.geoToberWallBuildUpDimensions]);

  // Build total-per-lineal-metre: specId -> itemId -> (net * rate)
  // Uses persisted API dimension items (real imported net values) — never falls back to mock
  const totalsBySpec = useMemo(() => {
    const buildUpData = (tender?.groundworks?.geoToberWallBuildUpData || {}) as Record<string, any[]>;
    const geoToberWallBuildUpDimensions = ((tender?.groundworks as any)?.geoToberWallBuildUpDimensions || {}) as Record<string, Array<{ id?: string; description: string; net: number; unit: string; rate?: number }>>;
    const map: Record<string, Record<string, number>> = {};

    geoToberWallSpecifications.forEach((spec) => {
      const savedItems = buildUpData[spec.id] || [];
      map[spec.id] = {};
      const codeMap =
        spec.type === "geowall"
          ? GEO_WALL_BUILD_UP_ITEM_CODE_MAP
          : TOBERMORE_BUILD_UP_ITEM_CODE_MAP;
      const dimensionItems = geoToberWallBuildUpDimensions[spec.id];
      // Use real imported items only — no mock fallback
      if (!dimensionItems || dimensionItems.length === 0) return;

      dimensionItems.forEach((item) => {
        const itemId = item.id || '';
        const evaluateCode = codeMap[itemId];
        const libraryRate = evaluateCode ? getRateFromLibrary(evaluateCode) : 0;
        const savedItem = savedItems.find((s: any) => s.id === itemId);
        const rate = savedItem && savedItem.rate !== 0 ? savedItem.rate : (item.rate && item.rate !== 0 ? item.rate : libraryRate);
        map[spec.id][itemId] = (item.net || 0) * rate;
      });
    });

    return map;
  }, [tender?.groundworks?.geoToberWallBuildUpData, (tender?.groundworks as any)?.geoToberWallBuildUpDimensions, getRateFromLibrary]);

  // Calculate per-plot values
  const { tableData, totals, activeItems } = useMemo(() => {
    const tableRows = tender?.groundworks?.geoToberWallTable || [];

    const itemTotals: Record<string, number> = {};
    allBuildUpItems.forEach((item) => {
      itemTotals[item.id] = 0;
    });

    const rows = tableRows.map((plotData: any, index: number) => {
      const row: Record<string, any> = {
        key: plotData.key || String(index),
        plotNo: plotData.plotNo || String(index + 1),
        housetype: plotData.housetype || "",
      };

      allBuildUpItems.forEach((buildUpItem) => {
        let itemValue = 0;
        geoToberWallSpecifications.forEach((wallSpec) => {
          const fieldName = specIdToField[wallSpec.id];
          const plotWallQty = fieldName ? (plotData[fieldName] as number) || 0 : 0;
          const buildUpTotal = totalsBySpec[wallSpec.id]?.[buildUpItem.id] ?? 0;
          itemValue += plotWallQty * buildUpTotal;
        });
        row[buildUpItem.id] = itemValue;
        itemTotals[buildUpItem.id] += itemValue;
      });

      return row;
    });

    // Only show items that have any non-zero value across all plots
    const active = allBuildUpItems.filter((item) => itemTotals[item.id] !== 0);

    return { tableData: rows, totals: itemTotals, activeItems: active };
  }, [
    tender?.groundworks?.geoToberWallTable,
    allBuildUpItems,
    totalsBySpec,
  ]);

  // Totals row
  const totalsRow = useMemo(() => {
    const row: Record<string, any> = {
      key: "__totals__",
      plotNo: "TOTAL",
      housetype: "",
    };
    activeItems.forEach((item) => {
      row[item.id] = totals[item.id];
    });
    return row;
  }, [activeItems, totals]);

  // Build dynamic columns
  const columns = useMemo(() => {
    const cols: any[] = [
      {
        title: "Plot No",
        dataIndex: "plotNo",
        key: "plotNo",
        width: 80,
        fixed: "left" as const,
        render: (value: string, record: any) =>
          record.key === "__totals__" ? <strong>{value}</strong> : value,
      },
      {
        title: "House Type",
        dataIndex: "housetype",
        key: "housetype",
        width: 120,
        fixed: "left" as const,
      },
    ];

    activeItems.forEach((item) => {
      cols.push({
        title: item.description,
        dataIndex: item.id,
        key: item.id,
        align: "right" as const,
        render: (value: number, record: any) => {
          const display = `£${Math.abs(value || 0).toFixed(2)}`;
          return record.key === "__totals__" ? (
            <strong>{display}</strong>
          ) : (
            display
          );
        },
      });
    });

    return cols;
  }, [activeItems]);

  const dataSource = tableData.length > 0 ? [...tableData, totalsRow] : [];

  return (
    <div className="space-y-4">
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        size="small"
        bordered
        scroll={{ x: "max-content" }}
        className="geo-tober-wall-quants-table"
        locale={{
          emptyText:
            "No quantities calculated. Add Geowall or Tobermore data in the takeoff tabs first.",
        }}
        rowClassName={(record) =>
          record.key === "__totals__" ? "bg-blue-50" : ""
        }
      />
    </div>
  );
};

export default GeoToberWallQuants;
